/**
 * Lightweight HTML sanitizer to replace dangerouslySetInnerHTML usage.
 * Strips script tags, event handlers, and other XSS vectors while
 * preserving safe formatting tags.
 */

const ALLOWED_TAGS = new Set([
  'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
  'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'img', 'hr', 'small', 'sub', 'sup', 'code', 'pre',
]);

const ALLOWED_ATTRS = new Set([
  'href', 'title', 'alt', 'src', 'class', 'id', 'target',
  'rel', 'style', 'width', 'height', 'colspan', 'rowspan',
]);

// Regex to strip dangerous constructs
const SCRIPT_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
const EVENT_ATTR_REGEX = /\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_URL_REGEX = /(?:href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*'|javascript:[^\s>]+)/gi;

/**
 * Sanitize HTML string by removing scripts, event handlers, and javascript: URLs.
 * Returns safe HTML suitable for dangerouslySetInnerHTML.
 * For full safety, prefer using textContent/innerText instead.
 */
export function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return '';

  let cleaned = html;

  // 1. Remove <script> tags entirely
  cleaned = cleaned.replace(SCRIPT_REGEX, '');

  // 2. Remove onclick, onload, onerror, etc.
  cleaned = cleaned.replace(EVENT_ATTR_REGEX, '');

  // 3. Remove javascript: URLs
  cleaned = cleaned.replace(JS_URL_REGEX, (match) => {
    return match.replace(/javascript:/gi, '#blocked');
  });

  return cleaned;
}

/**
 * Strip ALL HTML tags and return plain text.
 * Safest option for user-supplied content.
 */
export function stripHTML(html) {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export default sanitizeHTML;
